import { type Virtualizer } from '@tanstack/react-virtual';
import { useEffect, useRef } from 'react';

export type UseGetTopVisibleElementIndexParams = {
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  onTopVisibleElementChange: (index: number) => void;
};

/**
 * This hook determines which element is first visible element.
 * It checks how much of scroll viewport each element takes and then returns index of element that takes the largest space
 */
export const useGetTopVisibleElementIndex = ({
  onTopVisibleElementChange,
  virtualizer,
}: UseGetTopVisibleElementIndexParams) => {
  const topVisibleElementIndexRef = useRef<number | null>(null);

  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;

  const onTopVisibleElementChangeRef = useRef(onTopVisibleElementChange);
  onTopVisibleElementChangeRef.current = onTopVisibleElementChange;

  useEffect(() => {
    const scroll = () => {
      window.requestAnimationFrame(() => {
        if (!virtualizerRef.current || !virtualizerRef.current.range || !virtualizerRef.current.scrollRect) return;

        const range = virtualizerRef.current.range;
        const scrollTopPos = (virtualizerRef.current.scrollOffset ?? 0) - virtualizerRef.current.options.scrollMargin;
        const scrollHeight = virtualizerRef.current.scrollRect.height;
        const scrollBottomPos = scrollTopPos + scrollHeight;

        let maxViewportPercentTaken = 0;
        let visibleIndex = -1;

        if (range) {
          const virtualItems = virtualizerRef.current.getVirtualItems();
          for (let i = range.startIndex; i <= range.endIndex; i++) {
            const item = virtualItems.find((item) => item.index === i);
            if (!item) continue;
            const itemTop = item.start > scrollTopPos ? item.start : scrollTopPos;
            const itemBottom = item.start + item.size < scrollBottomPos ? item.start + item.size : scrollBottomPos;
            const itemHeightInView = itemBottom - itemTop;
            const viewportPercentTaken = itemHeightInView / scrollHeight;

            if (viewportPercentTaken >= 0.5) {
              visibleIndex = i;
              break;
            } else if (viewportPercentTaken > maxViewportPercentTaken) {
              visibleIndex = i;
              maxViewportPercentTaken = viewportPercentTaken;
            }
          }

          if (visibleIndex !== topVisibleElementIndexRef.current) {
            onTopVisibleElementChangeRef.current(visibleIndex);
            topVisibleElementIndexRef.current = visibleIndex;
          }
        }
      });
    };

    virtualizerRef.current.scrollElement?.addEventListener('scroll', scroll);

    return () => {
      virtualizerRef.current.scrollElement?.removeEventListener('scroll', scroll);
    };
  }, []);
};
